import { UserRole } from '@heidi/schema/ml-scribe';
import {
  useCombinedQueriesTeamGeneralInfo,
  useCurrentWorkspace,
  useQueryUser,
} from '@heidi/workspace/data';

import { HEIDI_2_AWARENESS_BANNER_MODAL_ID } from '../../../contracts/heidi-2';
import { getHeidi2Ports, resolveHeidi2WelcomeOutcome } from '../../../data/heidi-2';

import { computeShouldShowHeidi2ActivatedWelcome } from './heidi-2-activated-welcome-eligibility';
import {
  computeHasFullTeamEvidenceAccess,
  computeShouldShowHeidi2OptIn,
  isClinicianTrialModalBlockingHeidi2OptIn,
} from './heidi-2-opt-in-eligibility';

/**
 * When this page load started. `getUserBillingInfo` is in
 * `PERSISTED_QUERY_KEYS`, so it rehydrates from localStorage as `success`
 * carrying the PREVIOUS session's payload and only then refetches — a window
 * in which a Heidi 2 account still reads as v1.
 */
const APP_STARTED_AT = Date.now();

/**
 * `activate`: an admin still on Heidi 1, whose tour switches the workspace over.
 * `activated`: anyone on a team already on Heidi II, whose tour must not.
 */
export type Heidi2WelcomeCohort = 'activate' | 'activated';

/** Which Heidi II welcome this viewer is due, or `null` for none. */
export const useHeidi2WelcomeCohort = (): Heidi2WelcomeCohort | null => {
  const ports = getHeidi2Ports();
  const isHeidi2OptInEnabled = ports.useIsHeidi2OptInEnabled();
  const isHeidi2OnboardingEnabled = ports.useIsHeidi2OnboardingEnabled();
  const isWorkspaceIdSettled = ports.useWorkspaceIdSettledForCreditUsagePopoverLaunch();
  const {
    data: billingInfo,
    isPending: isBillingInfoPending,
    dataUpdatedAt: billingInfoUpdatedAt,
  } = ports.useUserBillingInfo();
  // Restored-from-storage billing reads as settled, so the cohort gate below
  // would run against last session's `heidi_version` — mounting the tour for
  // an account already on Heidi 2 and firing `activate-heidi-2` off a stale
  // answer. Nothing shows until a response from THIS load has landed.
  const hasFreshBillingInfo = billingInfoUpdatedAt >= APP_STARTED_AT;
  const modalAppState = ports.useModalLaunchState();
  const seenModalIds = ports.useSeenModalIds();
  // Same inputs that unblock `launchModalsAtom`. Until they settle,
  // CLINICIAN_TRIAL is not in the queue yet and this surface would win the race.
  const isClinicianTrialEligibilityLoaded =
    modalAppState.isUserDataLoaded &&
    !modalAppState.isUserPending &&
    !modalAppState.isKindePropertiesPending &&
    seenModalIds !== null &&
    !isBillingInfoPending &&
    hasFreshBillingInfo;
  // Rematch containment: timer not read; session status + Chronicle still suppress.
  const isClinicalRecordingActive = ports.useIsClinicalRecordingActive(false);

  const currentWorkspace = useCurrentWorkspace();
  const isWorkspaceAdmin = currentWorkspace?.user_role === UserRole.ADMIN;
  const { hasTeam, isTeamDataReady, teamDetails } = useCombinedQueriesTeamGeneralInfo();
  const team = teamDetails?.team;
  const { data: user } = useQueryUser((user) =>
    user
      ? {
          isManaged: user.is_managed,
          welcomeView: user.seen_modals?.[HEIDI_2_AWARENESS_BANNER_MODAL_ID],
        }
      : undefined
  );
  const isManaged = user?.isManaged;
  // Admin-only endpoints. Skip them unless this viewer can see the cohort:
  // opt-in on, and not managed (unknown counts as managed).
  const shouldLoadTeamEvidence = isHeidi2OptInEnabled && isManaged === false;
  const teamId =
    shouldLoadTeamEvidence && isWorkspaceAdmin && team?.id ? team.id : undefined;
  const teamEntitlementsQuery = ports.useQueryTeamAccessEntitlements(teamId);
  const roleIdsCanAccessEvidence =
    team?.team_evidence_settings?.role_ids_can_access_evidence;
  // Empty allow-list is unrestricted, so the role catalog is only needed when
  // the list might be "every role" rather than a subset.
  const needsRoleCatalog = (roleIdsCanAccessEvidence ?? []).some(
    (id) => typeof id === 'string' && id.length > 0
  );
  const rolesQuery = ports.useQueryRoles(needsRoleCatalog ? teamId : undefined);

  const currentQueuedModalId = ports.useCurrentQueuedModalId();
  const queueHeadModalId = ports.useQueueHeadModalId();
  const teamEntitlements = !isTeamDataReady
    ? undefined
    : !hasTeam
      ? []
      : teamEntitlementsQuery.isError ||
          teamEntitlementsQuery.isPending ||
          teamEntitlementsQuery.data === undefined
        ? undefined
        : (teamEntitlementsQuery.data.team_entitlements ?? []);
  const teamRoleIds =
    !needsRoleCatalog ||
    rolesQuery.isError ||
    rolesQuery.isPending ||
    rolesQuery.data?.data == null
      ? undefined
      : rolesQuery.data.data.map((role) => role.id);
  const hasFullTeamEvidenceAccess = computeHasFullTeamEvidenceAccess({
    isTeamMembershipKnown: isTeamDataReady,
    hasTeam,
    teamEntitlements,
    accessMode: team?.team_evidence_settings?.access_mode,
    roleIdsCanAccessEvidence,
    teamRoleIds,
  });
  const isClinicianTrialModalBlocking = isClinicianTrialModalBlockingHeidi2OptIn({
    currentQueuedModalId,
    queueHeadModalId,
  });
  const isActivatingAdmin = computeShouldShowHeidi2OptIn(
    {
      isHeidi2OptInEnabled,
      isWorkspaceIdSettled,
      isClinicalRecordingActive,
      isUserManaged: isManaged,
      isWorkspaceAdmin,
      hasFullTeamEvidenceAccess,
      isClinicianTrialModalBlocking,
      isClinicianTrialEligibilityLoaded,
      isMaxBillingSeat: ports.isMaxBillingSeat,
    },
    billingInfo
  );
  if (isActivatingAdmin) return 'activate';

  const isJoiningActivatedTeam = computeShouldShowHeidi2ActivatedWelcome({
    isHeidi2OnboardingEnabled,
    isClinicianTrialEligibilityLoaded,
    isWorkspaceIdSettled,
    isClinicalRecordingActive,
    isClinicianTrialModalBlocking,
    isUserManaged: isManaged,
    hasTeam,
    heidiVersion: billingInfo?.heidi_version,
    welcomeOutcome: resolveHeidi2WelcomeOutcome(user?.welcomeView),
  });
  return isJoiningActivatedTeam ? 'activated' : null;
};
